import * as React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormSpacer, InformationPopUp } from 'components';
import { useContentContext } from '@qbe/dfc-content';
import { FormDataType } from 'contexts/FormProvider';
import { CustomerType, LANGUAGES, VULNERABILITIES } from '../enum';
import { TextValueType, YesNoList, SEPARATOR } from 'utils/constants';
import { FormGroupLabel } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { DropdownController, TextFieldController, ToggleController } from 'controllers';
import { useContentComplaintPartners } from 'utils/content';

interface OtherDetailProps {
  form: UseFormReturn<FormDataType>;
  children?: React.ReactNode;
  isFieldRequired: (fieldName: string) => boolean;
}

const OtherDetail = ({ form, children, isFieldRequired }: OtherDetailProps) => {
  const { control, watch, getValues, register } = form;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const TEXT_CONTENT = {
    heading: getTextContent('insured.otherDetails.heading'),
    vulnerability: getTextContent('insured.otherDetails.vulnerability'),
    vulnerabilityInfoText: getTextContent('insured.otherDetails.vulnerability.infoText'),
    vulnerabilityInfo: getTextContent('insured.otherDetails.vulnerabilityInfo'),
    vulnerabilityReasonLabel: getTextContent('insured.otherDetails.vulnerabilityReason.label'),
    languageInterpreter: getTextContent('insured.otherDetails.languageInterpreter'),
    language: getTextContent('insured.otherDetails.language'),
    languageLabel: getTextContent('insured.otherDetails.language.label'),
    languageType: getTextContent('insured.otherDetails.languageType'),
    otherLanguageLabel: getTextContent('insured.otherDetails.otherLanguage.label'),
    information: getTextContent('insured.otherDetails.information'),
    customerDescLabel: getTextContent('insured.otherDetails.customerDesc.label'),
    businessName: getTextContent('insured.otherDetails.businessName'),
    businessNameLabel: getTextContent('insured.otherDetails.businessName.label'),
    isSmallBusiness: getTextContent('insured.otherDetails.isSmallBusiness'),
    smallBusinessInfoText: getTextContent('insured.otherDetails.smallBusiness.infoText'),
  };

  const showCustomerDetails = watch('insuredIndOrBus');
  const isSmallBusiness = watch('isSmallBusiness') || null;
  const isVulnerableCustomer = watch('isVulnerableCustomer');
  const isLanguageInterpreter = watch('isLanguageInterpreter');

  React.useEffect(() => {
    register('language');
    register('vulnerabilityReason');
  }, [register]);

  return (
    <FormCard title={TEXT_CONTENT.heading} variant="sectionTitle">
      <ToggleController
        name="isVulnerableCustomer"
        control={control}
        required
        title={TEXT_CONTENT.vulnerability}
        titleSuffix={<InformationPopUp message={TEXT_CONTENT.vulnerabilityInfoText} />}
        options={YesNoList}
        capture
      />

      {isVulnerableCustomer === 'yes' && (
        <>
          <FormGroupLabel
            alignCenter
            required={isFieldRequired('vulnerabilityReason')}
            title={TEXT_CONTENT.vulnerabilityInfo}
          />
          <FormSpacer type="input" />
          <DropdownController
            control={control}
            capture
            label={TEXT_CONTENT.vulnerabilityReasonLabel}
            name="vulnerabilityReason"
            optionList={VULNERABILITIES.map((value) => ({
              label: value,
              value,
            }))}
          />
        </>
      )}

      {watch('vulnerabilityReason') === 'Language barriers' && isVulnerableCustomer === 'yes' && (
        <>
          <FormSpacer type="formgroup" />

          <Controller
            name="isLanguageInterpreter"
            control={control}
            render={({ field }) => (
              <Toggle
                {...field}
                required={isFieldRequired('isLanguageInterpreter')}
                title={TEXT_CONTENT.languageInterpreter}
                id={field.name}
                optionList={YesNoList.map((option: TextValueType) => ({
                  autoComplete: 'off',
                  id: `${field.name}${SEPARATOR}${option.value}`,
                  value: option.value,
                  label: option.text ?? option.value,
                  defaultChecked: isLanguageInterpreter === option.value,
                  'data-test': 'isLanguageInterpreter',
                  'data-track': 'isLanguageInterpreter',
                  'data-track-value': 'capture',
                }))}
              />
            )}
          />

          {watch('isLanguageInterpreter') === 'yes' && (
            <>
              <FormSpacer type="formgroup" />
              <FormGroupLabel alignCenter required={isFieldRequired('language')} title={TEXT_CONTENT.language} />

              <DropdownController
                control={control}
                capture
                label={TEXT_CONTENT.languageLabel}
                name="language"
                optionList={LANGUAGES.map((value) => ({
                  label: value,
                  value,
                }))}
              />
              {watch('language') === 'Other' && (
                <>
                  <FormGroupLabel
                    alignCenter
                    required={isFieldRequired('otherLanguage')}
                    title={TEXT_CONTENT.languageType}
                  />
                  <FormSpacer type="input" />
                  <TextFieldController
                    name="otherLanguage"
                    control={control}
                    maxLength={50}
                    capture
                    label={TEXT_CONTENT.otherLanguageLabel}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                </>
              )}
            </>
          )}
        </>
      )}

      <FormGroupLabel alignCenter required={isFieldRequired('customerDesc')} title={TEXT_CONTENT.information} />
      <FormSpacer type="input" />
      <TextFieldController
        multiline
        name="customerDesc"
        control={control}
        label={TEXT_CONTENT.customerDescLabel}
        maxLength={4000}
      />

      {showCustomerDetails === CustomerType.BUSINESS && (
        <>
          <FormGroupLabel alignCenter required={isFieldRequired('businessName')} title={TEXT_CONTENT.businessName} />
          <FormSpacer type="input" />
          <TextFieldController
            name="businessName"
            control={control}
            maxLength={100}
            label={TEXT_CONTENT.businessNameLabel}
            capture
            inputProps={{
              minLength: 1,
              maxLength: 100,
            }}
          />

          <ToggleController
            name="isSmallBusiness"
            control={control}
            required={isFieldRequired('isSmallBusiness')}
            title={TEXT_CONTENT.isSmallBusiness}
            titleSuffix={
              <InformationPopUp
                message={TEXT_CONTENT.smallBusinessInfoText}
                dataTrack="infoIconCustomerSmallBusiness"
              />
            }
            options={YesNoList}
            capture
          />
        </>
      )}

      {children}
    </FormCard>
  );
};

export default OtherDetail;
